import { ReactElement, useMemo } from 'react'
import BugReportIcon from '@mui/icons-material/BugReport'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import { Theme } from '@mui/material/styles'
import { useSelectedElementProblems } from '@editorHooks'
import ElementProblemsList from '../ElementProblemsList'
import FormCollapsibleSection from './FormCollapsibleSection'
import { formSectionIconSx } from './icons'

interface Props {
  elementGuid: string
  selectedOpeningGuid?: string | null
  defaultExpanded?: boolean
  dataCy?: string
  headerBackgroundColor?: string | ((theme: Theme) => string)
}

const CollapsibleProblemsBoxSection = ({
  elementGuid,
  selectedOpeningGuid,
  defaultExpanded = true,
  dataCy,
  headerBackgroundColor,
}: Props): ReactElement | null => {
  const { filteredProblems, isEmpty, hasError, hasWarning } = useSelectedElementProblems({
    selectedElement: elementGuid,
    selectedOpeningGuid,
  })

  const resolvedHeaderBackgroundColor = useMemo(() => {
    if (headerBackgroundColor) return headerBackgroundColor
    if (hasError) return (theme: Theme) => theme.scenePalette.invalid as string
    if (hasWarning) return (theme: Theme) => theme.scenePalette.warning as string
    return undefined
  }, [headerBackgroundColor, hasError, hasWarning])
  const resolvedHeaderTextColor = useMemo(() => {
    if (hasError) return '#ffffff'
    if (hasWarning) return '#000000'
    return undefined
  }, [hasError, hasWarning])

  const startIcon = useMemo(() => {
    const Icon = hasError ? BugReportIcon : WarningAmberOutlinedIcon

    return (
      <Icon
        sx={{
          ...formSectionIconSx,
          color: resolvedHeaderTextColor ?? 'grey.600',
        }}
      />
    )
  }, [hasError, resolvedHeaderTextColor])

  const labelText = useMemo(() => {
    if (hasError && !hasWarning) return 'Probleme'
    if (!hasError && hasWarning) return 'Warnungen'
    if (hasError && hasWarning) return 'Probleme / Warnungen'
    return 'Probleme / Warnungen'
  }, [hasError, hasWarning])

  return (
    <FormCollapsibleSection
      label={labelText}
      startIcon={startIcon}
      defaultExpanded={defaultExpanded}
      dataCy={dataCy}
      headerBackgroundColor={resolvedHeaderBackgroundColor}
      headerTextColor={resolvedHeaderTextColor}
      isEmpty={isEmpty}
    >
      <ElementProblemsList problems={filteredProblems} />
    </FormCollapsibleSection>
  )
}

export default CollapsibleProblemsBoxSection
